import { makeAutoObservable } from 'mobx';
import { inject, injectable } from 'inversify';
import { AXIOS_INSTANCE } from '@shared/api/reactQuery';
import { notifications } from '@shared/utils/notifications';
import logger from 'js-logger';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';

// Интерфейсы
export interface TrustedUser {
  phone: string;
  note: string;
  readOnly: boolean;
}

export interface AddTrustedUserFormValues {
  phone: string;
  note: string;
  isReadOnly: boolean;
}

export interface OsiTrustedUsersWidgetViewModel {
  trustedUsers: TrustedUser[];
  isLoading: boolean;
  osiId: number | null | undefined;
  loadTrustedUsers: () => Promise<void>;
  addTrustedUser: (data: AddTrustedUserFormValues) => Promise<boolean>;
  deleteTrustedUser: (phone: string) => Promise<boolean>;
  formatPhoneDisplay: (phoneNumber: string) => string;
  cleanPhoneForApi: (phone: string) => string;
}

export const OsiTrustedUsersWidgetViewModelToken = Symbol.for('OsiTrustedUsersWidgetViewModel');

@injectable()
export class OsiTrustedUsersVm implements OsiTrustedUsersWidgetViewModel {
  trustedUsers: TrustedUser[] = [];

  isLoading = false;

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);
    this.loadTrustedUsers();
  }

  get osiId(): number | undefined {
    return this.osiModule?.osiId;
  }

  // Загрузка списка доверенных пользователей
  loadTrustedUsers = async () => {
    if (!this.osiId) return;

    this.isLoading = true;
    try {
      const data: any = await AXIOS_INSTANCE.get(`/api/Osi/${this.osiId}/trusted-users`);

      if (data?.code !== 0) {
        notifications.error(data?.message || 'Не удалось загрузить список доверенных пользователей');
        this.trustedUsers = [];
        return;
      }

      if (data?.result && Array.isArray(data.result)) {
        this.trustedUsers = data.result;
      } else {
        this.trustedUsers = [];
      }
    } catch (e) {
      logger.error(e);
      notifications.error('Не удалось загрузить список доверенных пользователей');
    } finally {
      this.isLoading = false;
    }
  };

  // Добавление доверенного пользователя
  addTrustedUser = async (formData: AddTrustedUserFormValues): Promise<boolean> => {
    if (!this.osiId) return false;

    const phoneForApi = this.cleanPhoneForApi(formData.phone);

    try {
      const requestData = {
        note: formData.note.trim(),
        isReadOnly: formData.isReadOnly
      };

      const response: any = await AXIOS_INSTANCE.post(
        `/api/Osi/${this.osiId}/trusted-user?phone=${phoneForApi}`,
        requestData
      );

      if (response?.code !== 0) {
        notifications.error(response?.message || 'Не удалось добавить доверенного пользователя');
        return false;
      }

      notifications.success('Доверенный пользователь успешно добавлен');
      await this.loadTrustedUsers();
      return true;
    } catch (e: any) {
      logger.error(e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Не удалось добавить доверенного пользователя';
      notifications.error(errorMessage);
      return false;
    }
  };

  // Удаление доверенного пользователя
  deleteTrustedUser = async (userPhone: string): Promise<boolean> => {
    if (!this.osiId) return false;

    try {
      const response: any = await AXIOS_INSTANCE.delete(`/api/Osi/${this.osiId}/trusted-user?phone=${userPhone}`);

      if (response?.code !== 0) {
        notifications.error(response?.message || 'Не удалось удалить доверенного пользователя');
        return false;
      }

      notifications.success('Доверенный пользователь удален');
      await this.loadTrustedUsers();
      return true;
    } catch (e: any) {
      logger.error(e);
      const errorMessage = e?.response?.data?.message || e?.message || 'Не удалось удалить доверенного пользователя';
      notifications.error(errorMessage);
      return false;
    }
  };

  // Очистка номера телефона для API (убирает первую 7)
  cleanPhoneForApi = (maskedPhone: string): string => {
    const digits = maskedPhone.replace(/\D/g, '');
    return digits.startsWith('7') ? digits.slice(1) : digits;
  };

  // Форматирование номера телефона для отображения
  formatPhoneDisplay = (phoneNumber: string): string => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+7 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 8)}-${cleaned.slice(8, 10)}`;
    }
    if (cleaned.length === 11) {
      return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(
        9,
        11
      )}`;
    }
    return phoneNumber;
  };
}
